import type { AnalyticsAdapter } from './AnalyticsAdapter'
import type { AnalyticsEvent, AnalyticsEventName } from './events'
import { CloudflareAnalyticsTransport, type AnalyticsTransport } from './CloudflareAnalyticsTransport'

const forbiddenSensitiveKeys = /^(lat|lng|latitude|longitude|pickup|dropoff|coordinates?|pickup(latitude|longitude|location)?|dropoff(latitude|longitude|location)?|query|transcript|destinationid|url|krideurl|uberurl)$/i

function sanitizeValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sanitizeValue)
  if (value && typeof value === 'object') {
    return sanitize(value as Record<string, unknown>)
  }
  return value
}

function sanitize(metadata: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(metadata).map(([key, value]) => [
    key,
      forbiddenSensitiveKeys.test(key.replaceAll(/[-_]/g, '')) ? '[REDACTED]' : sanitizeValue(value),
  ]))
}

export class ConsoleAnalyticsAdapter implements AnalyticsAdapter {
  private events: AnalyticsEvent[] = []
  private listeners = new Set<() => void>()

  constructor(private readonly transport?: AnalyticsTransport) {}

  track(name: AnalyticsEventName, metadata: Record<string, unknown> = {}): void {
    const event = { name, timestamp: new Date().toISOString(), metadata: sanitize(metadata) }
    this.events = [...this.events.slice(-49), event]
    console.info('[K-VIBE analytics]', event)
    this.transport?.enqueue(event)
    this.listeners.forEach((listener) => listener())
  }

  getRecent(): AnalyticsEvent[] {
    return this.events
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }
}

const cloudflareTransport = typeof window === 'undefined'
  ? undefined
  : new CloudflareAnalyticsTransport()

export const analytics = new ConsoleAnalyticsAdapter(cloudflareTransport)
